import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'
import { makeStyles, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import AdminPage from '../../../../admin/shared/AdminPage'
import SydButton from '../../../../../commonDesign/Button'
import Loading from '../../../../../molecules/Loading'
import { useGetAiEvalRun, useCancelEvalRun, useCreateEvalRun } from '../../../../../../api/denaliAssistant'
import StatusBadge from '../shared/StatusBadge'
import ScoreCard from './ScoreCard'
import HighlightCard from './HighlightCard'
import ErroredSamplesCard from './ErroredSamplesCard'
import MetricCard from './MetricCard'
import RunConfigCard from './RunConfigCard'
import SamplesTable from './SamplesTable'

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-md) var(--padding-lg)'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--gap-md)'
  },
  title: {
    fontSize: 'var(--text-size-lg)',
    fontWeight: 600,
    color: 'var(--color-neutral-800)'
  },
  subtitle: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    flexWrap: 'wrap'
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  actions: {
    display: 'flex',
    gap: 'var(--gap-sm)'
  },
  highlightsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'var(--gap-md)'
  },
  metricsCard: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    padding: 'var(--padding-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-sm)'
  },
  metricsTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 'var(--gap-md)'
  }
}))

function isErrored (sample) {
  return sample.status === 'error' || (sample.status !== 'complete' && (sample.judgeError || sample.lastError))
}

function buildRerunPayload (run) {
  return {
    ...(run.name && { name: run.name }),
    prompt: run.prompt,
    rubric: run.rubric,
    judgeSchema: run.judgeSchema,
    sampleCount: run.sampleCount,
    ...(run.judgeModelId && { judgeModelId: run.judgeModelId }),
    ...(run.scoreWeights && Object.keys(run.scoreWeights).length > 0 && { scoreWeights: run.scoreWeights }),
    context: run.context || {}
  }
}

function EvalRunDetail ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const { batchId } = useParams()
  const numericBatchId = parseInt(batchId, 10)

  const { data: run, isLoading } = useGetAiEvalRun(numericBatchId)
  const cancelEvalRun = useCancelEvalRun()
  const createEvalRun = useCreateEvalRun()

  const [toast, setToast] = useState(null)
  const [rerunning, setRerunning] = useState(false)

  const samples = useMemo(() => run?.samples || [], [run?.samples])

  const erroredSamples = useMemo(() => samples.filter(isErrored), [samples])

  const { bestSample, worstSample } = useMemo(() => {
    const scored = samples
      .filter(s => s.weightedScore !== null && s.weightedScore !== undefined)
      .slice()
      .sort((a, b) => b.weightedScore - a.weightedScore)
    return {
      bestSample: scored[0] || null,
      worstSample: scored.length > 1 ? scored[scored.length - 1] : null
    }
  }, [samples])

  const metricEntries = useMemo(() => {
    const agg = run?.results?.aggregate || {}
    return Object.entries(agg)
      .filter(([key, e]) => key !== '_weightedScore' && (e.kind === 'boolean' || e.kind === 'number'))
      .sort((a, b) => a[0].localeCompare(b[0]))
  }, [run])

  const handleCancel = async () => {
    try {
      await cancelEvalRun.mutateAsync({ batchId: numericBatchId })
      setToast({ severity: 'success', message: 'Run cancellation requested.' })
    } catch (err) {
      setToast({ severity: 'error', message: err?.message || 'Cancel failed' })
    }
  }

  const handleRerun = async () => {
    if (!run) return
    setRerunning(true)
    try {
      const result = await createEvalRun.mutateAsync(buildRerunPayload(run))
      const newId = result?.batchId
      if (newId) {
        history.push(`${root}/ai/evals/${newId}`)
      } else {
        setToast({ severity: 'success', message: 'Re-run started.' })
      }
    } catch (err) {
      setToast({ severity: 'error', message: err?.message || 'Re-run failed' })
    } finally {
      setRerunning(false)
    }
  }

  if (isLoading || !run) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <div className={classes.page}>
        <div className={classes.headerBar}>
          <div>
            <div className={classes.title}>{run.name || `Eval run #${run.batchId}`}</div>
            <div className={classes.subtitle}>
              <span className={classes.mono}>#{run.batchId}</span>
              <span>{'\u00B7'}</span>
              <StatusBadge status={run.status} />
              <span>{'\u00B7'}</span>
              <span>{samples.length} sample{samples.length === 1 ? '' : 's'}</span>
            </div>
          </div>
          <div className={classes.actions}>
            <SydButton size='sm' variant='secondary' onClick={() => history.push(`${root}/ai/evals`)}>
              Back to list
            </SydButton>
            {!run.isProcessing && !run.isCancelled && (
              <SydButton
                size='sm'
                variant='primary'
                onClick={handleRerun}
                disabled={rerunning || createEvalRun.isLoading}
              >
                {rerunning ? 'Starting\u2026' : 'Re-run'}
              </SydButton>
            )}
            {run.isProcessing && !run.isCancelled && (
              <SydButton
                size='sm'
                variant='outline'
                priority='warning'
                onClick={handleCancel}
                disabled={cancelEvalRun.isLoading}
              >
                {cancelEvalRun.isLoading ? 'Cancelling\u2026' : 'Cancel run'}
              </SydButton>
            )}
          </div>
        </div>

        <ScoreCard results={run.results} samples={samples} cost={run.cost} />

        <ErroredSamplesCard samples={erroredSamples} />

        {(bestSample || worstSample) && (
          <div className={classes.highlightsGrid}>
            <HighlightCard flavor='best' sample={bestSample} />
            <HighlightCard flavor='worst' sample={worstSample} />
          </div>
        )}

        {metricEntries.length > 0 && (
          <div className={classes.metricsCard}>
            <span className={classes.metricsTitle}>Metrics</span>
            <div className={classes.metricsGrid}>
              {metricEntries.map(([name, entry]) => (
                <MetricCard key={name} name={name} entry={entry} samples={samples} />
              ))}
            </div>
          </div>
        )}

        <RunConfigCard run={run} />

        <SamplesTable samples={samples} />
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert
            severity={toast.severity}
            variant='filled'
            elevation={6}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </AdminPage>
  )
}

EvalRunDetail.propTypes = {
  root: PropTypes.string
}

export default EvalRunDetail
